import { useEffect, useRef, useState } from 'react';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Alert,
} from '@mui/material';
import { ActionButton } from 'components/ui/buttons';
import SeatingChart from 'components/SeatingChart';
import fetchExpresso from 'utility/fetchExpresso';
import { useNotification } from 'context/notification';
import { capitalizeWord } from 'utility/strings';

export default function OrderSeats({ eventId, tranId }) {
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [seats, setSeats] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [channelKeys, setChannelKeys] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [reconciliationIssue, setReconciliationIssue] = useState(null);

    const reconciliationMessage =
        reconciliationIssue?.eventId === eventId && reconciliationIssue?.tranId === tranId
            ? reconciliationIssue.message
            : null;

    const { notify } = useNotification();

    const chartRef = useRef(null);

    useEffect(() => {
        if (!eventId || !tranId) return;

        Promise.all([
            getOrderSeats(eventId, tranId),
            getSeatProducts(eventId),
            getSeatCategories(eventId),
            getSeatChannels(eventId),
        ])
            .then(([seats, products, categories, channelKeys]) => {
                setSeats(seats);
                setProducts(products);
                setCategories(categories);
                setChannelKeys(channelKeys);
                setStatus('success');
            })
            .catch(() => {
                setStatus('error');
            });
    }, [eventId, tranId, refresh]);

    const hasSections = seats.some((seat) => !!seat.section);

    const handleOrderUpdate = async () => {
        if (reconciliationMessage) return;
        try {
            if (!chartRef.current?.chart?.listOrderChanges) {
                notify.error('We are unable to update your order at this time');
                return;
            }

            // Get changes from the chart component
            const changes = await chartRef.current.chart.listOrderChanges();

            // Set details of modified seats
            for (const change of changes) {
                change.ticketType = isNaN(change.ticketType) ? null : Number(change.ticketType); // product ID has to be a number

                const seatInfo = await chartRef.current.chart.findObject(change.object);
                const seat = createAssignedSeat(seatInfo);
                change.object = seat.object; // Note: 'object' from changes array is a string. We are replacing it with 'object' from createAssignedSeat which is an object
                change.section = seat.section;
                change.row = seat.row;
                change.seat = seat.seat;
                change.seats = seat.seats;
            }

            // Update order on the api
            const response = await fetchExpresso(
                `/apiv2/events/${eventId}/orders/${tranId}/seats/`,
                {
                    method: 'PUT',
                    body: { changes, channelKeys },
                },
            );

            if (response.status === 200) {
                notify.success('Order Updated');
                setIsEditing(false);
                setRefresh((r) => r + 1);
            } else {
                const data = await response.json().catch(() => ({}));
                if (data.code === 'season_seat_reconciliation_required') {
                    setReconciliationIssue({
                        eventId,
                        tranId,
                        message:
                            data.message ||
                            'Seat changes need reconciliation. Contact support before retrying this update.',
                    });
                    setIsEditing(false);
                    return;
                }
                notify.error("We're experiencing a temporary issue. Please try again later.");
            }
        } catch (e) {
            notify.error('Unable to update order. Please check your connection and try again.');
        }
    };

    const renderContent = () => {
        if (reconciliationMessage) {
            return <Alert severity="error">{reconciliationMessage}</Alert>;
        }
        if (isEditing) {
            const order = [];

            for (const seat of seats) {
                if (seat.tableBookingKey === null) {
                    // Regular Seat or General Admission Area
                    order.push({
                        label: seat.id,
                        ticketType: String(seat.productId),
                    });
                } else {
                    // Booked Table
                    if (order.findIndex((o) => o.label === seat.tableBookingKey) === -1) {
                        order.push({
                            label: seat.tableBookingKey,
                            ticketType: String(seat.productId),
                        });
                    }
                }
            }

            const pricing = {
                prices: categories.map((cat) => ({
                    category: cat.key,
                    ticketTypes: products
                        .filter((p) => p.seat_category_id === cat.key)
                        .map((p) => ({
                            ticketType: String(p.product_id),
                            label: p.prod_name,
                            price: p.price,
                        })),
                })),
                priceFormatter: function (price) {
                    return '$' + price.toFixed(2);
                },
            };

            return (
                <SeatingChart
                    eventId={eventId}
                    modes={['editOrder']}
                    order={order}
                    pricing={pricing}
                    channelKeys={channelKeys}
                    chartRef={chartRef}
                />
            );
        }

        if (status === 'loading') {
            return (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                </Box>
            );
        }

        if (status === 'error') {
            return (
                <Typography color="error" variant="body2">
                    Unable to load seats for this order.
                </Typography>
            );
        }

        if (!seats.length) {
            return <Typography variant="body2">No seats found for this order.</Typography>;
        }

        return (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Ticket Type</TableCell>
                        {hasSections && <TableCell>Section</TableCell>}
                        <TableCell>Row</TableCell>
                        <TableCell>Seat</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {seats.map((seat, index) => (
                        <TableRow key={index}>
                            <TableCell>{seat.product}</TableCell>
                            {hasSections && <TableCell>{seat.section}</TableCell>}
                            <TableCell>
                                {capitalizeWord(seat.rowType)} {seat.row}
                            </TableCell>
                            <TableCell>{seat.seat}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    return (
        <Card>
            <CardHeader title="Selected Seats" subheader="View and Edit this Order's Seats" />
            <CardContent>{renderContent()}</CardContent>
            <CardActions>
                {isEditing === true && (
                    <ActionButton
                        disabled={status !== 'success'}
                        onClick={() => setIsEditing(false)}
                    >
                        Cancel
                    </ActionButton>
                )}
                {isEditing === true && (
                    <ActionButton disabled={status !== 'success'} onClick={handleOrderUpdate}>
                        Save Seats
                    </ActionButton>
                )}
                {isEditing === false && (
                    <ActionButton
                        disabled={status !== 'success' || Boolean(reconciliationMessage)}
                        onClick={() => setIsEditing(true)}
                    >
                        Edit Seats
                    </ActionButton>
                )}
            </CardActions>
        </Card>
    );
}

// API Methods

async function getOrderSeats(eventId, tranId) {
    const response = await fetchExpresso(`/apiv2/events/${eventId}/orders/${tranId}/seats/`);

    if (response.status === 200) {
        const results = await response.json();
        return results.seats || [];
    } else {
        throw new Error(`[${response.status}] Something went wrong`);
    }
}

async function getSeatProducts(eventId) {
    const response = await fetchExpresso(`/apiv2/events/${eventId}/seat-products`);

    if (response.status !== 200) {
        throw new Error('Unable to load seat products');
    }

    const products = await response.json();

    return products;
}

async function getSeatCategories(eventId) {
    const response = await fetchExpresso(`/apiv2/events/${eventId}/seat-categories`);

    if (response.status !== 200) {
        throw new Error('Unable to load seat categories');
    }

    const { categories } = await response.json();

    return categories;
}

async function getSeatChannels(eventId) {
    const response = await fetchExpresso(`/apiv2/events/${eventId}/seat-channels`);

    if (response.status !== 200) {
        throw new Error('Unable to load seat channels');
    }

    const { channelKeys } = await response.json();

    return channelKeys;
}

// SeatsIO Utility Functions

function createAssignedSeat(obj) {
    return {
        object: {
            key: obj.id,
            type: obj.objectType,
            uuid: obj.uuid,
        },
        section: {
            type: getSectionType(obj),
            label: obj.labels?.section || null,
        },
        row: {
            type: getRowType(obj),
            label: obj.labels?.parent || null,
        },
        seat: {
            type: getSeatType(obj),
            label: obj.labels?.own || null,
        },
        seats:
            obj.seats?.map((o) => ({
                object: {
                    key: o.id,
                    type: o.objectType,
                    uuid: o.uuid,
                },
                section: {
                    type: getSectionType(o),
                    label: o.labels?.section,
                },
                row: {
                    type: getRowType(o),
                    label: o.labels?.parent,
                },
                seat: {
                    type: getSeatType(o),
                    label: o.labels?.own,
                },
            })) || [],
    };
}

function getSectionType(obj) {
    return obj.labels?.section ? 'section' : null;
}

function getRowType(obj) {
    if (obj.parent?.type) {
        return String(obj.parent?.type).toLowerCase();
    } else {
        return null;
    }
}

function getSeatType(obj) {
    if (obj.objectType === 'GeneralAdmissionArea') {
        return 'ga';
    } else if (obj.objectType) {
        return String(obj.objectType).toLowerCase();
    } else {
        return null;
    }
}
